import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Send, Bot, User, Sparkles, RefreshCw, Trash2,
  Plus, Lightbulb, Loader2, Zap, ExternalLink,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useAppData } from '@/context/AppDataContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Tailwind-styled markdown components (tables, headings, lists, code)
const mdComponents = {
  table: ({ node, ...props }) => (
    <div className="overflow-x-auto my-2">
      <table className="text-xs w-full border-collapse" {...props} />
    </div>
  ),
  thead: ({ node, ...props }) => <thead className="bg-[#e5e5e5]/70" {...props} />,
  th: ({ node, ...props }) => (
    <th className="px-3 py-1.5 text-left font-semibold border border-[#e5e5e5]/70 text-[#2a2a2a]" {...props} />
  ),
  td: ({ node, ...props }) => (
    <td className="px-3 py-1.5 border border-[#e5e5e5]/50 text-[#1a1a1a]" {...props} />
  ),
  tr: ({ node, ...props }) => (
    <tr className="even:bg-[#f5f5f5]/60" {...props} />
  ),
  h1: ({ node, ...props }) => <h1 className="text-base font-bold mt-3 mb-1 text-[#1a1a1a]" {...props} />,
  h2: ({ node, ...props }) => <h2 className="text-sm font-bold mt-3 mb-1 text-[#1a1a1a]" {...props} />,
  h3: ({ node, ...props }) => <h3 className="text-sm font-semibold mt-2 mb-0.5 text-[#1a1a1a]" {...props} />,
  ul: ({ node, ...props }) => <ul className="my-1 ml-4 list-disc space-y-0.5 text-sm" {...props} />,
  ol: ({ node, ...props }) => <ol className="my-1 ml-4 list-decimal space-y-0.5 text-sm" {...props} />,
  li: ({ node, ...props }) => <li className="leading-relaxed" {...props} />,
  p: ({ node, ...props }) => <p className="my-0.5 text-sm leading-relaxed" {...props} />,
  strong: ({ node, ...props }) => <strong className="font-semibold" {...props} />,
  em: ({ node, ...props }) => <em className="italic" {...props} />,
  code: ({ node, inline, ...props }) =>
    inline
      ? <code className="bg-[#e5e5e5]/70 px-1 rounded text-xs font-mono" {...props} />
      : <pre className="bg-[#e5e5e5]/70 p-2 rounded text-xs font-mono overflow-x-auto my-2 whitespace-pre-wrap" {...props} />,
  blockquote: ({ node, ...props }) => (
    <blockquote className="border-l-4 border-[#ffd87a] pl-3 my-2 text-[#4a4a4a] italic text-sm" {...props} />
  ),
  hr: () => <hr className="my-2 border-[#e5e5e5]" />,
};

export default function AIChatPage() {
  const { user } = useAuth();
  const { aiSuggestions } = useAppData();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (aiSuggestions.length > 0 && suggestions.length === 0) {
      setSuggestions(aiSuggestions.map(s => s.data?.text || s.label));
    }
  }, [aiSuggestions]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchSessions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ai/chat/sessions`);
      setSessions(res.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const loadSession = async (sessionId) => {
    try {
      const res = await axios.get(`${API}/ai/chat/sessions/${sessionId}/messages`);
      setMessages(res.data.map(m => ({ ...m, steps: [], sources: [] })));
      setCurrentSession(sessionId);
    } catch {
      toast.error('Failed to load chat history');
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setCurrentSession(null);
    setSuggestions([
      "What deals are currently in due diligence?",
      "Show me my startup portfolio and current valuations",
      "What is my portfolio XIRR and Sharpe ratio?",
      "Which properties do we own and what's the rental yield?",
    ]);
  };

  const sendMessage = async (text = message) => {
    if (!text.trim() || loading) return;

    const userMsg = {
      id: Date.now().toString(),
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
      steps: [],
      sources: [],
    };
    setMessages(prev => [...prev, userMsg]);
    setMessage('');
    setLoading(true);

    const placeholderId = `ph-${Date.now()}`;
    setMessages(prev => [...prev, {
      id: placeholderId,
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      steps: [],
      sources: [],
      streaming: true,
    }]);

    try {
      const res = await fetch(`${API}/ai/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: text, session_id: currentSession }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulatedText = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) {
          // Process any remaining buffer on stream close
          if (buffer.trim().startsWith('data: ')) {
            try {
              const ev = JSON.parse(buffer.slice(buffer.indexOf('data: ') + 6).trim());
              if (ev.type === 'done') {
                setMessages(prev => prev.map(m =>
                  m.id === placeholderId
                    ? { ...m, streaming: false, sources: ev.sources || [] }
                    : m
                ));
                setSuggestions(ev.suggestions || []);
                fetchSessions();
              }
            } catch { /* ignore */ }
          }
          break;
        }
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          let ev;
          try { ev = JSON.parse(line.slice(6)); } catch { continue; }

          if (ev.type === 'start') {
            setCurrentSession(ev.session_id);

          } else if (ev.type === 'tool_call') {
            setMessages(prev => prev.map(m =>
              m.id === placeholderId
                ? { ...m, steps: [...(m.steps || []), ev.message] }
                : m
            ));

          } else if (ev.type === 'chunk') {
            accumulatedText += ev.content;
            setMessages(prev => prev.map(m =>
              m.id === placeholderId
                ? { ...m, content: accumulatedText, streaming: true }
                : m
            ));

          } else if (ev.type === 'done') {
            setMessages(prev => prev.map(m =>
              m.id === placeholderId
                ? { ...m, streaming: false, sources: ev.sources || [] }
                : m
            ));
            setSuggestions(ev.suggestions || []);
            fetchSessions();

          } else if (ev.type === 'error') {
            setMessages(prev => prev.map(m =>
              m.id === placeholderId
                ? { ...m, content: `Sorry, something went wrong: ${ev.message}`, streaming: false }
                : m
            ));
            toast.error(ev.message || 'AI response failed');
          }
        }
      }

    } catch (err) {
      setMessages(prev => prev.filter(m => m.id !== placeholderId));
      setMessages(prev => prev.filter(m => m.id !== userMsg.id));
      toast.error('Failed to get AI response');
    } finally {
      setLoading(false);
    }
  };

  const deleteSession = async (sessionId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/ai/chat/sessions/${sessionId}`);
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      if (currentSession === sessionId) startNewChat();
      toast.success('Chat deleted');
    } catch {
      toast.error('Failed to delete chat');
    }
  };

  const formatTime = (ts) =>
    new Date(ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

  const formatDate = (ts) =>
    new Date(ts).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });

  return (
    <div className="flex h-screen overflow-hidden" data-testid="ai-chat-page">

      {/* ── Sidebar ── */}
      <aside className="w-72 bg-white border-r border-[#e5e5e5] flex flex-col overflow-hidden">
        <div className="p-4 border-b border-[#e5e5e5]">
          <Button onClick={startNewChat}
            className="w-full bg-[#ffd87a] hover:bg-[#f5cc6a] text-black">
            <Plus className="w-4 h-4 mr-2" /> New Chat
          </Button>
        </div>
        <ScrollArea className="flex-1 p-2">
          <div className="space-y-1">
            {sessions.map(session => (
              <div
                key={session.id}
                className={`flex items-start justify-between p-3 rounded-lg cursor-pointer transition-all ${
                  currentSession === session.id
                    ? 'bg-[#ffd87a]/10 border border-[#ffd87a]/30'
                    : 'hover:bg-[#f5f5f5] border border-transparent'
                }`}
                onClick={() => loadSession(session.id)}
              >
                <div className="flex-1 min-w-0 pr-2">
                  <p className="text-sm text-[#1a1a1a] leading-snug line-clamp-2">{session.title}</p>
                  <p className="text-xs text-[#4a4a4a] mt-0.5">{formatDate(session.updated_at)}</p>
                </div>
                <button
                  onClick={(e) => deleteSession(session.id, e)}
                  className="flex-shrink-0 p-1 rounded hover:bg-[#e5e5e5] text-[#4a4a4a] hover:text-red-400 transition-colors mt-0.5"
                  title="Delete chat"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </ScrollArea>
      </aside>

      {/* ── Main Chat Area ── */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header */}
        <header className="bg-white/80 backdrop-blur-sm border-b border-[#e5e5e5] px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#dae4c4] flex items-center justify-center shadow-sm">
              <Bot className="w-5 h-5 text-[#1a1a1a]" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-[#1a1a1a]">OlafAI</h1>
              <p className="text-xs text-[#4a4a4a] flex items-center gap-1">
                <Zap className="w-3 h-3 text-amber-500" />
                Agentic — queries live platform data
              </p>
            </div>
          </div>
        </header>

        {/* Messages */}
        <ScrollArea className="flex-1 p-6">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br bg-[#ffd87a]/15 flex items-center justify-center mb-6">
                <Sparkles className="w-10 h-10 text-[#ffd87a]" />
              </div>
              <h2 className="text-2xl font-bold text-[#1a1a1a] mb-2">
                Ask me anything about your portfolio
              </h2>
              <p className="text-[#4a4a4a] mb-8 max-w-md">
                I query live platform data — deals, startups, holdings, real estate, analytics, approvals, and more.
              </p>
              <div className="grid grid-cols-2 gap-3 max-w-lg">
                {suggestions.map((s, i) => (
                  <button key={i} onClick={() => sendMessage(s)}
                    className="p-4 rounded-xl bg-white/80 border border-[#e5e5e5] hover:border-[#ffd87a]/50 text-left transition-all group">
                    <div className="flex items-start gap-3">
                      <Lightbulb className="w-5 h-5 text-[#ffd87a] mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-[#4a4a4a] group-hover:text-[#1a1a1a]">{s}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-6 max-w-4xl mx-auto">
              {messages.map(msg => (
                <div key={msg.id} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'items-start'}`}>

                  {msg.role === 'assistant' && (
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br bg-[#1a1a1a] flex items-center justify-center flex-shrink-0 mt-1">
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                  )}

                  {/* Message column: bubble + sources stacked */}
                  <div className={`flex flex-col gap-2 ${msg.role === 'user' ? 'items-end max-w-[50%]' : 'max-w-[50%] min-w-0'}`}>

                    {/* Bubble */}
                    <div className={`rounded-2xl px-4 py-3 ${
                      msg.role === 'user'
                        ? 'bg-[#ffd87a] text-black'
                        : 'bg-[#f5f5f5] text-[#1a1a1a] w-full'
                    }`}>

                      {/* Tool thinking steps */}
                      {msg.role === 'assistant' && msg.steps?.length > 0 && (
                        <div className="space-y-1 mb-3 pb-2 border-b border-[#e5e5e5]">
                          {msg.steps.map((step, i) => (
                            <p key={i} className="text-xs text-[#1a1a1a] flex items-center gap-1.5">
                              {msg.streaming && i === msg.steps.length - 1 && !msg.content
                                ? <Loader2 className="w-3 h-3 animate-spin flex-shrink-0" />
                                : <span className="text-emerald-500 flex-shrink-0">✓</span>
                              }
                              {step}
                            </p>
                          ))}
                        </div>
                      )}

                      {/* Content */}
                      {msg.role === 'assistant' && !msg.content && msg.streaming ? (
                        <div className="flex gap-1 py-1">
                          <div className="w-2 h-2 bg-[#ffd87a] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <div className="w-2 h-2 bg-[#ffd87a] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <div className="w-2 h-2 bg-[#ffd87a] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      ) : msg.role === 'assistant' ? (
                        <div className="prose prose-sm max-w-none text-[#1a1a1a]">
                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                            {msg.content}
                          </ReactMarkdown>
                        </div>
                      ) : (
                        <p className="text-sm leading-relaxed">{msg.content}</p>
                      )}

                      <p className={`text-xs mt-2 ${msg.role === 'user' ? 'text-[#ffd87a]' : 'text-[#4a4a4a]'}`}>
                        {formatTime(msg.timestamp)}
                      </p>
                    </div>

                    {/* Sources — rendered BELOW the bubble, always outside it */}
                    {msg.role === 'assistant' && msg.sources && msg.sources.length > 0 && (
                      <div className="w-full">
                        <p className="text-xs text-[#4a4a4a] font-medium mb-1.5 flex items-center gap-1">
                          <ExternalLink className="w-3 h-3" />
                          Web sources
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                          {msg.sources.map((src, i) => (
                            <a
                              key={i}
                              href={src.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-start gap-2 p-2.5 rounded-lg bg-white border border-[#e5e5e5] hover:border-[#ffd87a]/50 hover:shadow-sm transition-all no-underline group"
                            >
                              <div className="w-4 h-4 rounded bg-[#f5f5f5] flex items-center justify-center flex-shrink-0 mt-0.5">
                                <ExternalLink className="w-2.5 h-2.5 text-[#ffd87a]" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-xs font-medium text-[#2a2a2a] group-hover:text-[#1a1a1a] line-clamp-2 leading-tight">
                                  {src.title}
                                </p>
                                <p className="text-xs text-[#4a4a4a] mt-0.5 truncate">
                                  {(() => { try { return new URL(src.url).hostname.replace('www.', ''); } catch { return src.url; } })()}
                                </p>
                              </div>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {msg.role === 'user' && (
                    <div className="w-8 h-8 rounded-lg bg-[#e5e5e5] flex items-center justify-center flex-shrink-0 mt-1">
                      <User className="w-4 h-4 text-[#4a4a4a]" />
                    </div>
                  )}
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </ScrollArea>

        {/* Suggestions bar */}
        {messages.length > 0 && suggestions.length > 0 && !loading && (
          <div className="px-6 py-3 border-t border-[#e5e5e5]/50">
            <div className="flex items-center gap-2 overflow-x-auto pb-1">
              <span className="text-xs text-[#4a4a4a] whitespace-nowrap">Ask:</span>
              {suggestions.map((s, i) => (
                <button key={i} onClick={() => sendMessage(s)}
                  className="px-3 py-1.5 rounded-full bg-[#f5f5f5] border border-[#e5e5e5] text-xs text-[#4a4a4a] hover:border-[#ffd87a]/50 hover:text-[#1a1a1a] whitespace-nowrap transition-all">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <div className="p-4 border-t border-[#e5e5e5] bg-white/50">
          <div className="max-w-4xl mx-auto flex gap-3">
            <Input
              value={message}
              onChange={e => setMessage(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !loading && sendMessage()}
              placeholder="Ask about deals, portfolio, startups, real estate..."
              className="flex-1 bg-white border-[#e5e5e5] text-[#1a1a1a] placeholder:text-[#4a4a4a] focus:border-[#ffd87a]"
              disabled={loading}
            />
            <Button
              onClick={() => sendMessage()}
              disabled={loading || !message.trim()}
              className="bg-[#ffd87a] hover:bg-[#f5cc6a] text-black px-6"
            >
              {loading
                ? <RefreshCw className="w-4 h-4 animate-spin" />
                : <Send className="w-4 h-4" />
              }
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
